/*
 * Entity references in agent prose.
 *
 * The backend names every case, client, document, record, reminder, intake,
 * draft, file, form, packet, plan or thread with a markdown link whose href is
 * an app path (`caseblink/case_agent/refs.py` is the writer; this is the
 * reader). The chat renders those links as inline citations; anything that is
 * not an entity path stays an ordinary link.
 *
 * Path grammar (keep in sync with refs.py):
 *
 *   /clients/{fn}                                              client
 *   /clients/{fn}/cases/{ind}/workspace                        case (canonical)
 *   /cases/{ind}/workspace                                     case (no client)
 *   …/workspace?open={draft|file|form|packet|plan}:{id}        case artifact
 *   …/workspace?thread={id}                                    case thread
 *   /clients/{fn}?tab=documents&documentId={id}                document
 *   /clients/{fn}?tab=reminders&reminderId={id}                reminder
 *   /clients/{fn}?tab=outgoing_intakes&open=intake:{id}       intake (legacy: oi_mode=view&oi_id={id})
 *   /clients/{fn}?tab=outgoing_intakes&open=template:{id}     intake template (`saved:{id}` for the firm's)
 *   /clients/{fn}?tab={tab}&{focusKey}={id}                    record
 *   /clients/{fn}?agent=client[&thread={id}]                   client / client thread (panel)
 *   /home?agent=practice[&thread={id}]                         practice / practice thread (panel)
 *   /firm-knowledge?entry={id}                                 firm library entry
 *   /firm-knowledge?tab=intake_templates&template={id}         firm intake template
 *   /companies/{id}                                            company
 *   /companies/{id}?tab=playbook&rule={n}                      company playbook rule
 *   /time?entry={id}                                           time entry
 *
 * Legacy (older links; still recognised):
 *
 *   /clients/{fn}?agent=client_manager&thread={id}             client thread
 *
 * No React or IO here so the grammar stays unit-testable.
 */
import { parseCasePath, parseOpenParam } from "@/features/caseIde/routes";

export type EntityKind =
  | "practice"
  | "client"
  | "case"
  | "document"
  | "record"
  | "reminder"
  | "intake"
  | "template"
  | "draft"
  | "file"
  | "form"
  | "packet"
  | "plan"
  | "thread"
  | "library"
  | "company"
  | "rule"
  | "time_entry";

/** Which history a `record` ref points at (its client-profile focus key). */
export type RecordFocusKey =
  | "addressHistoryId"
  | "employmentHistoryId"
  | "immigrationStatusHistoryId"
  | "immigrationEntryExitHistoryId"
  | "dependentId";

export type EntityRefTarget = {
  kind: EntityKind;
  /** The href as written, for navigation. */
  path: string;
  /** The client the entity belongs to, when the path says. */
  clientId?: string;
  /** The case the entity belongs to, when the path says. */
  individualId?: string;
  /** The company a company or playbook rule path is under. */
  companyId?: string;
  /** The entity's own id (absent for a bare client or case). */
  id?: string;
  /** For `record`: which history. */
  focusKey?: RecordFocusKey;
};

const ARTIFACT_KINDS: ReadonlySet<string> = new Set([
  "draft",
  "file",
  "form",
  "packet",
  "plan"
]);

const RECORD_FOCUS_KEYS: readonly RecordFocusKey[] = [
  "addressHistoryId",
  "employmentHistoryId",
  "immigrationStatusHistoryId",
  "immigrationEntryExitHistoryId",
  "dependentId"
];

const RECORD_LABEL: Record<RecordFocusKey, string> = {
  addressHistoryId: "Address",
  employmentHistoryId: "Employment",
  immigrationStatusHistoryId: "Immigration status",
  immigrationEntryExitHistoryId: "Travel",
  dependentId: "Dependent"
};

const KIND_LABEL: Record<EntityKind, string> = {
  practice: "Practice",
  client: "Client",
  case: "Case",
  document: "Document",
  record: "Record",
  reminder: "Reminder",
  intake: "Intake",
  template: "Template",
  draft: "Draft",
  file: "File",
  form: "Form",
  packet: "Packet",
  plan: "Plan",
  thread: "Thread",
  library: "Library entry",
  company: "Company",
  rule: "Playbook rule",
  time_entry: "Time entry"
};

const dec = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** True for an app-relative href (`/x`), false for `//cdn`, `https://`, `mailto:`… */
export const isAppPath = (href: string): boolean =>
  href.startsWith("/") && !href.startsWith("//");

/**
 * Recognise an entity path. Returns null for anything else (external URLs,
 * anchors, app paths that are not entities), which the chat renders as a
 * plain link.
 */
export const parseEntityHref = (
  href: string | null | undefined
): EntityRefTarget | null => {
  const raw = (href ?? "").trim();
  if (!raw || !isAppPath(raw)) return null;
  const q = raw.indexOf("?");
  const pathname = q >= 0 ? raw.slice(0, q) : raw;
  const search = q >= 0 ? raw.slice(q) : "";
  const params = new URLSearchParams(search);
  const thread = (params.get("thread") ?? "").trim();
  const segments = pathname.split("/").filter(Boolean).map(dec);

  // The practice in the panel (`/home?agent=practice`).
  if (
    segments.length === 1 &&
    segments[0] === "home" &&
    params.get("agent") === "practice"
  ) {
    return thread
      ? { kind: "thread", path: raw, id: thread }
      : { kind: "practice", path: raw };
  }

  // Cases and their artifacts / threads.
  const asCase = parseCasePath(pathname);
  if (asCase && !asCase.legacy && asCase.segments[0] === "workspace") {
    const base = {
      path: raw,
      individualId: asCase.ref.individualId,
      clientId: asCase.ref.foreignNationalId ?? undefined
    };
    if (thread) return { kind: "thread", id: thread, ...base };
    const open = parseOpenParam(search);
    if (open && ARTIFACT_KINDS.has(open.kind)) {
      return { kind: open.kind as EntityKind, id: open.id, ...base };
    }
    if (open) return null; // `settings:` is a pane, not an entity
    return { kind: "case", ...base };
  }

  // Clients and everything on the profile.
  if (segments[0] === "clients" && segments.length === 2 && segments[1]) {
    const clientId = segments[1];
    const base = { path: raw, clientId };
    if (thread) return { kind: "thread", id: thread, ...base };
    const tab = (params.get("tab") ?? "").trim();
    if (!tab) return { kind: "client", ...base };
    const documentId = (params.get("documentId") ?? "").trim();
    if (tab === "documents" && documentId) {
      return { kind: "document", id: documentId, ...base };
    }
    const reminderId = (params.get("reminderId") ?? "").trim();
    if (tab === "reminders" && reminderId) {
      return { kind: "reminder", id: reminderId, ...base };
    }
    if (tab === "outgoing_intakes") {
      // Current form: open=intake:<id> / submission:<intakeId>/<submissionId>;
      // the old panel's oi_id still resolves.
      const open = (params.get("open") ?? "").trim();
      const template = /^template:(.+)$/.exec(open);
      if (template) return { kind: "template", id: template[1], ...base };
      const m = /^(intake|preview|submission):(.+)$/.exec(open);
      const openId = m ? m[2].split("/")[0] : "";
      const intakeId = openId || (params.get("oi_id") ?? "").trim();
      if (intakeId) return { kind: "intake", id: intakeId, ...base };
    }
    for (const focusKey of RECORD_FOCUS_KEYS) {
      const id = (params.get(focusKey) ?? "").trim();
      if (id) return { kind: "record", id, focusKey, ...base };
    }
    // A tab without a focused item is still the client.
    return { kind: "client", ...base };
  }

  // A firm library entry (the library with its drawer open) or a firm intake
  // template open in its editor.
  if (segments.length === 1 && segments[0] === "firm-knowledge") {
    const entry = (params.get("entry") ?? "").trim();
    if (entry) return { kind: "library", path: raw, id: entry };
    const template = (params.get("template") ?? "").trim();
    if (params.get("tab") === "intake_templates" && template) {
      return { kind: "template", path: raw, id: template };
    }
  }

  // A company, or one of its playbook rules, cited by its label ("Northwind 7").
  if (
    segments.length === 2 &&
    segments[0] === "companies" &&
    segments[1] &&
    segments[1] !== "new"
  ) {
    const companyId = segments[1];
    const rule = (params.get("rule") ?? "").trim();
    if (params.get("tab") === "playbook" && /^\d+$/.test(rule)) {
      return { kind: "rule", path: raw, id: rule, companyId };
    }
    return { kind: "company", path: raw, id: companyId, companyId };
  }

  // A time entry open on the timesheet.
  if (segments.length === 1 && segments[0] === "time") {
    const entry = (params.get("entry") ?? "").trim();
    if (entry) return { kind: "time_entry", path: raw, id: entry };
  }

  return null;
};

/** Human noun for a target: "Case", "Address", "Draft"… */
export const kindLabel = (
  target: Pick<EntityRefTarget, "kind" | "focusKey">
): string =>
  target.kind === "record" && target.focusKey
    ? RECORD_LABEL[target.focusKey]
    : KIND_LABEL[target.kind];

/**
 * The person a client or case ref is about, for its avatar. Case labels are
 * `{visa} for {applicant}` (refs.py `case_ref`); a custom label without
 * ` for ` falls back to the label itself.
 */
export const refPersonName = (kind: EntityKind, label: string): string => {
  const text = label.trim();
  if (kind !== "case") return text;
  const at = text.lastIndexOf(" for ");
  const applicant = at >= 0 ? text.slice(at + 5).trim() : "";
  return applicant || text;
};

/**
 * The markdown link title (`[label](path "title")`) is `Kind · detail · detail`.
 * Split it so the card can show the kind as a badge and the rest as text.
 */
export const splitRefTitle = (
  title: string | null | undefined
): { kind: string | null; detail: string | null } => {
  const parts = (title ?? "")
    .split("·")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return { kind: null, detail: null };
  const [kind, ...rest] = parts;
  return { kind, detail: rest.length ? rest.join(" · ") : null };
};

const TITLED_LINK =
  /\[((?:[^[\]\\]|\\.)*)\]\(((?:\/|https?:\/\/)[^)\s]*)(?:\s+"((?:[^"\\]|\\.)*)")?\)/g;

const unescape = (s: string): string => s.replace(/\\(.)/g, "$1");

/** Reduce `[label](path "title")` links to their label (plain-text surfaces). */
export const stripEntityRefs = (text: string): string =>
  text.replace(TITLED_LINK, (_m, label: string) => unescape(label));

export type RefSegment =
  | { type: "text"; text: string }
  | { type: "ref"; label: string; href: string; title: string | null };

/**
 * Split plain text (a person's message, a handoff brief) into runs and the
 * entity links in it, so they render as citations instead of raw markdown.
 * Links that are not entity paths stay text.
 */
export const segmentEntityRefs = (text: string): RefSegment[] => {
  const out: RefSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(TITLED_LINK)) {
    const [whole, label, href, title] = m;
    if (parseEntityHref(href)) {
      const start = m.index ?? 0;
      if (start > last)
        out.push({ type: "text", text: text.slice(last, start) });
      out.push({
        type: "ref",
        label: unescape(label),
        href,
        title: title == null ? null : unescape(title)
      });
      last = start + whole.length;
    }
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
};
